import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AlbumCard from '../components/AlbumCard';
import SongCard from '../components/SongCard';
import MoodboardCard from '../components/MoodboardCard';
import Button from '../components/Button';
import CreateAlbumModal from '../components/modals/CreateAlbumModal';
import CreateSongModal from '../components/modals/CreateSongModal';
import config from '../config';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('albums');
  const [albums, setAlbums] = useState([]);
  const [songs, setSongs] = useState([]);
  const [moodboards, setMoodboards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateAlbumModalOpen, setIsCreateAlbumModalOpen] = useState(false);
  const [isCreateSongModalOpen, setIsCreateSongModalOpen] = useState(false);

  // Fetch data from our serverless backend
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch albums
      const albumsResponse = await axios.get(`${config.API_URL}/albums`);
      setAlbums(albumsResponse.data || []);
      
      // Fetch songs
      const songsResponse = await axios.get(`${config.API_URL}/songs`);
      setSongs(songsResponse.data || []);
      
      // For now, we'll use dummy moodboards data since it's not implemented in the backend yet
      // This will be replaced when the moodboards API is ready
      setMoodboards([]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle successful creation of a new album
  const handleAlbumCreated = (newAlbum) => {
    setAlbums(prev => [newAlbum, ...prev]);
    // Switch to albums tab to show the new album
    setActiveTab('albums');
  };
  
  // Handle album deletion
  const handleAlbumDeleted = (albumId) => {
    // Remove the album from state
    setAlbums(prev => prev.filter(album => album.id !== albumId));
    
    // Also remove any songs that were in this album
    setSongs(prev => prev.filter(song => song.albumId !== albumId));
  };

  // Handle successful creation of a new song
  const handleSongCreated = (newSong) => {
    console.log('New song created:', newSong);
    // Find the album for this song
    const album = albums.find(album => album.id === newSong.albumId);
    
    // Add album title to the song object for display purposes
    const songWithAlbumTitle = {
      ...newSong,
      albumTitle: album ? album.title : 'Unknown Album'
    };
    
    setSongs(prev => [songWithAlbumTitle, ...prev]);
    // Switch to songs tab to show the new song
    setActiveTab('songs');
  };
  
  // Handle song deletion
  const handleSongDeleted = (songId) => {
    setSongs(prev => prev.filter(song => song.id !== songId));
  };

  return (
    <div className="bg-white min-h-screen -mt-8 pt-8 pb-16">
      <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Music Library</h1>
        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            onClick={() => setIsCreateAlbumModalOpen(true)}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Album
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsCreateSongModalOpen(true)}
            disabled={albums.length === 0}
            title={albums.length === 0 ? 'Create an album first' : ''}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Song
          </Button>
          <Button 
            variant="primary" 
            onClick={() => alert('Moodboards feature coming soon!')}
            className="bg-red-600 hover:bg-red-500 text-white transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            New Moodboard
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'albums' ? 'border-red-500 text-red-600 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('albums')}
            >
              Albums
            </button>
            <button
              className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'songs' ? 'border-red-500 text-red-600 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('songs')}
            >
              Songs
            </button>
            <button
              className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'moodboards' ? 'border-red-500 text-red-600 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
              onClick={() => setActiveTab('moodboards')}
            >
              Moodboards
            </button>
          </nav>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      )}

      {/* Error message */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <button 
            onClick={fetchData} 
            className="mt-2 bg-red-600 hover:bg-red-500 text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Albums tab */}
      {activeTab === 'albums' && !isLoading && (
        <div>
          {albums.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 mb-4">No albums yet. Create your first album!</p>
              <Button 
                variant="primary" 
                onClick={() => setIsCreateAlbumModalOpen(true)}
                className="bg-red-600 hover:bg-red-500 text-white transition-all duration-200"
              >
                Create Album
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map(album => (
                <AlbumCard key={album.albumId} album={album} onDelete={handleAlbumDeleted} />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Songs tab */}
      {activeTab === 'songs' && !isLoading && (
        <div>
          {songs.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 mb-4">No songs yet. {albums.length > 0 ? 'Create your first song!' : 'Create an album first, then add songs to it.'}</p>
              {albums.length > 0 && (
                <Button 
                  variant="primary" 
                  onClick={() => setIsCreateSongModalOpen(true)}
                  className="bg-red-600 hover:bg-red-500 text-white transition-all duration-200"
                >
                  Create Song
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {songs.map(song => {
                // Find the album title for this song if it exists
                const album = albums.find(album => album.id === song.albumId);
                
                // Make sure we have the thumbnailKey for proper display
                const songWithAlbumTitle = {
                  ...song,
                  albumTitle: album ? album.title : 'Unknown Album',
                  // Ensure thumbnailKey is passed if it exists
                  thumbnailKey: song.thumbnailKey || null,
                  // Ensure thumbnailUrl is passed if it exists
                  thumbnailUrl: song.thumbnailUrl || null
                };
                
                console.log('Rendering song with thumbnail info:', {
                  id: song.id,
                  title: song.title,
                  hasThumbKey: !!song.thumbnailKey,
                  hasThumbUrl: !!song.thumbnailUrl
                });
                
                return <SongCard key={song.id} song={songWithAlbumTitle} onDelete={handleSongDeleted} />;
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Moodboards tab */}
      {activeTab === 'moodboards' && !isLoading && (
        <div className="text-center py-10">
          <p className="text-gray-500 mb-4">Moodboards feature coming soon!</p>
        </div>
      )}

      {/* Modals */}
      <CreateAlbumModal 
        isOpen={isCreateAlbumModalOpen} 
        onClose={() => setIsCreateAlbumModalOpen(false)} 
        onSuccess={handleAlbumCreated} 
      />
      
      <CreateSongModal 
        isOpen={isCreateSongModalOpen} 
        onClose={() => setIsCreateSongModalOpen(false)} 
        onSuccess={handleSongCreated} 
      />
      </div>
    </div>
  );
};

export default Dashboard;
